import { useBookingLanguage } from "@/lib/bookingLanguage";
import { formatCurrency } from "@/lib/money";
import type { GuestPortalPayment } from "@/lib/guestPortalTypes";

export function PaymentSummary({
  payment,
  totalAmount,
  currency,
  compact = false,
}: {
  payment: GuestPortalPayment;
  totalAmount: string;
  currency: string;
  compact?: boolean;
}) {
  const inquiry = useBookingLanguage() === "inquiry";
  const paid = Number(payment.paid_amount);
  const open = Number(payment.open_amount);
  const total = Number(totalAmount);
  const stayPriceLabel = inquiry ? "Aufenthaltspreis" : "Buchungspreis";

  if (compact) {
    return (
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        {paid > 0 ? (
          <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-800">
            Bezahlt {formatCurrency(payment.paid_amount, currency)}
          </span>
        ) : null}
        {open > 0 ? (
          <span className="rounded-full bg-amber-50 px-2.5 py-1 font-medium text-amber-900">
            Offen {formatCurrency(payment.open_amount, currency)}
          </span>
        ) : paid === 0 && open === 0 && payment.invoice_count === 0 ? (
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">
            Rechnung folgt
          </span>
        ) : paid > 0 && open === 0 ? (
          <span className="rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-800">
            Ausgeglichen
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/80 p-4">
      <h3 className="text-sm font-semibold text-slate-900">Zahlungsübersicht</h3>
      <dl className="mt-3 space-y-2 text-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-slate-600">Gesamtbetrag Aufenthalt</dt>
          <dd className="font-medium text-slate-900">{formatCurrency(totalAmount, currency)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-slate-600">Bereits bezahlt</dt>
          <dd className="font-medium text-emerald-700">
            {formatCurrency(payment.paid_amount, currency)}
          </dd>
        </div>
        <div className="flex justify-between gap-4 border-t border-slate-200 pt-2">
          <dt className="font-medium text-slate-800">Noch offen</dt>
          <dd
            className={
              open > 0 ? "font-semibold text-amber-800" : "font-medium text-slate-600"
            }
          >
            {formatCurrency(payment.open_amount, currency)}
          </dd>
        </div>
      </dl>
      {open > 0 ? (
        <p className="mt-3 text-xs text-amber-900/90">
          Offene Beträge können Sie bei Anreise an der Rezeption begleichen.
        </p>
      ) : paid === 0 && payment.invoice_count === 0 ? (
        <p className="mt-3 text-xs text-slate-500">
          Sobald eine Rechnung erstellt wurde, sehen Sie hier den Zahlungsstand.
        </p>
      ) : null}
      {total > 0 && paid + open > 0 && Math.abs(paid + open - total) > 0.02 ? (
        <p className="mt-2 text-xs text-slate-500">
          Der Rechnungsbetrag kann vom {stayPriceLabel} abweichen (z. B. Zusatzleistungen).
        </p>
      ) : null}
    </div>
  );
}
